import { useState } from 'react';
import { Box, Container, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';
import { useContent } from '../../content/useContent';
import { RenderedHtml } from '../../content/RenderedHtml';

interface FaqI18nItem {
  q: string;
  a: string;
}

interface FaqRowData {
  q: string;
  answerHtml: string;
}

function FaqRow({ item, isOpen, onToggle }: { item: FaqRowData; isOpen: boolean; onToggle: () => void }) {
  return (
    <Box borderBottom="1px solid" borderColor="blackAlpha.100">
      <Flex
        as="button"
        type="button"
        w="full"
        align="center"
        justify="space-between"
        gap={4}
        py="20px"
        textAlign="left"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Text fontSize="md" fontWeight={600} color="onboarding.deepPlum">
          {item.q}
        </Text>
        <Box
          as="span"
          flexShrink={0}
          fontSize="2xl"
          lineHeight={1}
          color="onboarding.roseMid"
          transform={isOpen ? 'rotate(45deg)' : 'rotate(0deg)'}
          transition="transform 0.2s ease"
          aria-hidden
        >
          +
        </Box>
      </Flex>
      {isOpen && (
        <RenderedHtml html={item.answerHtml} fontSize="md" color="onboarding.deepPlum" lineHeight={1.3} pb="20px" />
      )}
    </Box>
  );
}

export function Faq() {
  const { t } = useTranslation();
  // CMS-driven when available; falls back to the bundled i18n copy so the section
  // always renders (prerender / SEO and offline-of-API both stay intact).
  const { data } = useContent('faq');
  const [openIndex, setOpenIndex] = useState(0);

  const i18nItems = (t('landing.faq.items', { returnObjects: true }) as FaqI18nItem[]) ?? [];
  const apiItems: FaqRowData[] = (data?.children ?? []).map((c) => ({
    q: c.title,
    answerHtml: c.bodyHtml || (c.summary ? `<p>${c.summary}</p>` : ''),
  }));
  const items: FaqRowData[] =
    apiItems.length > 0 ? apiItems : i18nItems.map((i) => ({ q: i.q, answerHtml: `<p>${i.a}</p>` }));

  return (
    <Box as="section" id="faq" bg="white">
      <Container maxW="1240px" py={{ base: 16, lg: 24 }}>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 10, lg: 16 }} align="flex-start">
          {/* Left: heading + final-CTA card */}
          <Stack flex={{ lg: '0 0 400px' }} spacing="28px" w="full">
            <Heading
              as="h2"
              fontFamily="heading"
              fontWeight={700}
              fontSize="display.lg"
              lineHeight={1.1}
              letterSpacing="-0.01em"
              color="onboarding.deepPlum"
            >
              {t('landing.faq.title')}
            </Heading>
            <Stack bg="onboarding.cream" borderRadius="10px" p={{ base: '24px', md: '28px' }} spacing="16px">
              <Text fontSize="xl" fontWeight={600} color="onboarding.deepPlum">
                {t('landing.faq.ctaCard.title')}
              </Text>
              <Text fontSize="xs" color="onboarding.deepPlum">
                {t('landing.faq.ctaCard.subtitle')}
              </Text>
              <Box>
                <CtaButton
                  to="/onboarding"
                  bg="onboarding.plum"
                  color="white"
                  borderRadius="pill"
                  height="48px"
                  px="28px"
                  fontSize="md"
                  fontWeight={600}
                  _hover={{ bg: 'onboarding.roseDeep' }}
                  withArrow
                  trackEventName="cta_click"
                  trackProps={{ section: 'faq' }}
                >
                  {t('landing.faq.ctaCard.cta')}
                </CtaButton>
              </Box>
            </Stack>
          </Stack>

          {/* Right: accordion */}
          <Box flex="1" w="full">
            {items.map((item, idx) => (
              <FaqRow
                key={item.q}
                item={item}
                isOpen={openIndex === idx}
                onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
              />
            ))}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}
